import type { RuleInput, TaskInput } from "@app/core";

import { draftRecurrenceToRuleInput } from "./draft-to-rule.js";

export type QuickAddInput = TaskInput & { recurrence?: RuleInput };

export class InvalidQuickAddDraftError extends Error {
  override name = "InvalidQuickAddDraftError";
}

/** Convert the model's loose ISO spelling into the same canonical UTC form
 * every other creation path stores, then apply the user's task defaults. */
export function quickAddInputFromDraft(
  draft: unknown,
  defaults: { durationMinutes: number; reminderOffsets: readonly number[] },
): QuickAddInput {
  if (!isRecord(draft)
    || typeof draft.title !== "string"
    || !draft.title.trim()
    || typeof draft.notes !== "string"
    || (draft.due_at !== null && typeof draft.due_at !== "string")
    || typeof draft.all_day !== "boolean"
    || !isPriority(draft.priority)) {
    throw new InvalidQuickAddDraftError("The parsed reminder details are invalid.");
  }

  const dueAt = normalizeDueAt(draft.due_at);
  const allDay = dueAt !== null && draft.all_day;
  const recurrence = dueAt ? draftRecurrenceToRuleInput(draft.recurrence) : null;

  return {
    title: draft.title.trim(),
    notes: draft.notes,
    due_at: dueAt,
    all_day: allDay,
    duration_minutes: allDay ? 1440 : Math.max(1, Math.round(defaults.durationMinutes)),
    priority: draft.priority,
    is_event: false,
    reminder_offsets: [...defaults.reminderOffsets],
    ...(recurrence ? { recurrence } : {}),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isPriority(value: unknown): value is NonNullable<TaskInput["priority"]> {
  return value === "none" || value === "low" || value === "medium" || value === "high";
}

function normalizeDueAt(value: string | null): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) {
    throw new InvalidQuickAddDraftError("The parsed reminder date is invalid.");
  }
  return parsed.toISOString();
}
